import { useCallback, useEffect, useState } from 'react';

import { getErrorMessage, getPrograms, Program } from '@/lib/api/program';
import { DEFAULT_SUPPLIER } from '@/lib/constants/suppliers';
import { nowJST } from '@/lib/date';

/**
 * Custom hook for program data management
 * Handles fetching and managing program list state
 *
 * @param initialDate Initial date
 * @param initialSupplier Initial supplier
 * @param monitorMode Monitor mode (fetches programs 3 hours before and after current time)
 */
export const useProgramData = (
  initialDate: Date | null = new Date(),
  initialSupplier: string = DEFAULT_SUPPLIER,
) => {
  // Program list
  const [programs, setPrograms] = useState<Program[]>([]);
  // Loading state
  const [loading, setLoading] = useState(true);
  // Error state
  const [error, setError] = useState<string | null>(null);
  // Selected date
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
  // Selected supplier
  const [supplier, setSupplier] = useState<string>(initialSupplier);

  // Function to fetch program list
  const fetchPrograms = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // In monitor mode, fetch programs 3 hours before and after current time
      const now = nowJST();

      // 3 hours before and after current time
      const threeHoursBefore = now.subtract(3, 'hour');
      const threeHoursAfter = now.add(3, 'hour');

      // Convert to ISO string (UTC)
      const startAt = threeHoursBefore.utc().toISOString();
      const endAt = threeHoursAfter.utc().toISOString();

      // Fetch program list from API
      const response = await getPrograms(startAt, endAt, supplier || undefined);
      setPrograms(response.data || []);
    } catch (err) {
      console.error('Failed to fetch programs:', err);
      // Convert error message to user-friendly format
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [selectedDate, supplier]);

  // Initial data fetch and refetch on filter changes
  useEffect(() => {
    fetchPrograms();
  }, [selectedDate, supplier, fetchPrograms]);

  // Date change handler
  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  // Supplier change handler
  const handleSupplierChange = (value: string) => {
    setSupplier(value);
  };

  return {
    // State
    programs,
    loading,
    error,
    selectedDate,
    supplier,

    // Actions
    setSelectedDate,
    setSupplier,
    handleDateChange,
    handleSupplierChange,
    refreshData: fetchPrograms,
  };
};

export default useProgramData;
